import { StyleSheet, View, ViewProps } from 'react-native'
import {
  useFloors,
  normalizeCompanyColorScheme,
  ProductOccupancyState,
  Workspace,
  WorkspaceTriggerState,
  useWorkspaces,
  type NormalizedCompanyColorScheme,
} from 'zens-store'
import { Color, Radius, Spacing, Text } from 'zens-storybook'
import { useEffect } from 'react'
import { useNumberParams } from 'zens-router'

import { RefreshInfo } from '../refresh'
import { FloorplanZoomView, FloorplanView, FloorplanZoomViewProps } from '../floorplan'

import { colorForSchemeValue, colorForTriggerValue } from './colorscheme'

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: Spacing.S,
    paddingTop: 48 + Spacing.S,
    paddingBottom: 70 + Spacing.S,
  },
  refresh: {
    position: 'absolute',
    top: Spacing.M,
    right: Spacing.M + Spacing.S,
  },
  skeletonMap: {
    backgroundColor: Color.sleepingGrey,
    borderRadius: Radius.S,
    margin: Spacing.S,
  },
})

interface Props extends FloorplanZoomViewProps {
  companyId: number
  buildingId: number
  floorId?: number
  autoRefresh?: boolean
  colorScheme: NormalizedCompanyColorScheme
}

const InsightsLiveFloorplan = (props: Props) => {
  const { companyId, buildingId, floorId = 0, autoRefresh, colorScheme, style, ...otherProps } = props
  const [_, setNumberParams] = useNumberParams()
  const floors = useFloors({ companyId, buildingId }, { enabled: !floorId })
  const workspaces = useWorkspaces(
    { companyId, filter: { floorId: [floorId] } },
    { refetchInterval: autoRefresh ? 60000 : false, enabled: !!floorId },
  )

  useEffect(() => {
    if (!floorId && floors.data?.length && floors.data.length > 0) {
      setNumberParams({ floorId: floors.data[0].id })
    }
  }, [floors.data, floorId])

  const workspaceItems = workspaces.data?.data
  const normalizedColorScheme = normalizeCompanyColorScheme(colorScheme)

  function colorForItem(workspace: Workspace) {
    return workspace?.triggerState
      ? colorForTriggerValue(WorkspaceTriggerState[workspace.triggerState].toLowerCase(), normalizedColorScheme)
      : workspace?.occupancyState === ProductOccupancyState.Occupied
        ? colorForSchemeValue(normalizedColorScheme.occupancy_colors.occupied)
        : workspace?.occupancyState === ProductOccupancyState.Free
          ? colorForSchemeValue(normalizedColorScheme.occupancy_colors.free)
          : Color.calmGrey
  }

  return (
    <>
      <FloorplanZoomView style={[styles.container, style]} {...otherProps}>
        <FloorplanView
          companyId={companyId}
          floorId={floorId}
          data={workspaceItems}
          itemColor={(item) => colorForItem(item as Workspace)}
        />
      </FloorplanZoomView>
      <RefreshInfo
        date={workspaces.dataUpdatedAt}
        onRefresh={!autoRefresh ? workspaces.refetch : undefined}
        loading={workspaces.isRefetching}
        style={styles.refresh}
      />
    </>
  )
}

interface SkeletonProps extends ViewProps {}
const Skeleton = (props: SkeletonProps) => {
  const { style, ...otherProps } = props
  return (
    <>
      <Text.Skeleton style={styles.refresh} />
      <View style={[styles.container, styles.skeletonMap, style]} {...otherProps} />
    </>
  )
}
InsightsLiveFloorplan.Skeleton = Skeleton

export default InsightsLiveFloorplan
